import React, { useMemo, useState } from "react";
import emailjs from "emailjs-com";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../../config/firebase";
import "./Wholesale.css";
import Seo from "../../components/Seo";

import { routeMeta } from "../../seo/staticRoutes";
import { trackLead } from "../../analytics/track";
const baseRates = [
  { min: 0, max: 99, unitPrice: 170 },
  { min: 100, max: 299, unitPrice: 160 },
  { min: 300, max: 599, unitPrice: 150 },
  { min: 600, max: Infinity, unitPrice: 142 },
];

export default function Wholesale() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    location: "",
    monthlyVolume: "",
    productFocus: "",
    message: "",
  });
  const [calculator, setCalculator] = useState({
    quantityKg: 50,
    caseSize: 10,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const estimated = useMemo(() => {
    const rate = baseRates.find(
      (range) => calculator.quantityKg >= range.min && calculator.quantityKg <= range.max
    );
    const unitPrice = rate ? rate.unitPrice : 170;
    return {
      unitPrice,
      total: calculator.quantityKg * unitPrice,
      priceLabel: `₹${unitPrice} / kg`,
    };
  }, [calculator.quantityKg]);

  const handleFormChange = (evt) => {
    const { name, value } = evt.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCalculatorChange = (evt) => {
    const { name, value } = evt.target;
    setCalculator((prev) => ({
      ...prev,
      [name]: Number(value),
    }));
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (submitting) return;

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setSubmitError("Please add your name, phone number and email so we can send your quote.");
      setStep(1);
      return;
    }

    const payload = {
      ...formData,
      estimatedVolume: `${calculator.quantityKg} kg`,
      estimatedValue: `₹${estimated.total.toLocaleString("en-IN")}`,
      estimatedRate: estimated.priceLabel,
      caseSize: `${calculator.caseSize} kg`,
      submittedAt: new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
    };

    setSubmitting(true);
    setSubmitError("");

    // Store first — the lead must survive even if the notification email fails.
    let stored = false;
    try {
      await addDoc(collection(db, "wholesaleLeads"), payload);
      stored = true;
    } catch (err) {
      console.error("Wholesale lead could not be saved", err);
    }

    // Reuses the contact-form template, so the variable names must stay name/email/subject/message.
    let notified = false;
    try {
      await emailjs.send(
        "service_cott3tr",
        "template_449qdup",
        {
          name: payload.name,
          email: payload.email,
          subject: `Wholesale enquiry — ${payload.company || payload.name} (${payload.estimatedVolume})`,
          message: [
            `Name: ${payload.name}`,
            `Business: ${payload.company || "—"}`,
            `Phone: ${payload.phone}`,
            `Email: ${payload.email}`,
            `Location: ${payload.location || "—"}`,
            `Estimated volume: ${payload.estimatedVolume} @ ${payload.estimatedRate}`,
            `Estimated value: ${payload.estimatedValue}`,
            `Case size: ${payload.caseSize}`,
            `Monthly volume target: ${payload.monthlyVolume || "—"}`,
            `Product focus: ${payload.productFocus || "—"}`,
            `Notes: ${payload.message || "—"}`,
          ].join("\n"),
        },
        "a2RAs1ZB10E12-kyC"
      );
      notified = true;
    } catch (err) {
      console.error("Wholesale notification email failed", err);
    }

    setSubmitting(false);

    if (stored || notified) {
      // Only counted once the lead is actually captured somewhere. The estimated
      // order value rides along because a 600kg enquiry and a 50kg one are worth
      // very different amounts, and a bare Lead count cannot tell them apart —
      // which is exactly what a value-optimised campaign needs to bid on.
      trackLead({
        value: estimated.total,
        volumeKg: calculator.quantityKg,
        productFocus: formData.productFocus,
      });
      setStep(3);
    } else {
      setSubmitError(
        "We couldn't send your request just now. Please WhatsApp us on 6264208349 and we'll pick it up straight away."
      );
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="wholesale-step-card">
            <h3>Step 1: Tell Us About Your Business</h3>
            <p>
              Enter the basics so our wholesale team can create a custom quote for your category and volume.
            </p>
            <div className="form-grid">
              <label>
                Full Name
                <input name="name" value={formData.name} onChange={handleFormChange} placeholder="Name" />
              </label>
              <label>
                Business Name
                <input
                  name="company"
                  value={formData.company}
                  onChange={handleFormChange}
                  placeholder="Company / Store"
                />
              </label>
              <label>
                Email Address
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="Email"
                />
              </label>
              <label>
                Phone / WhatsApp
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  placeholder="Phone number"
                />
              </label>
              <label>
                Location
                <input
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  placeholder="City, State"
                />
              </label>
            </div>
            {submitError && (
              <p className="wholesale-form-error" role="alert">
                {submitError}
              </p>
            )}
            <button className="btn-professional-primary" onClick={() => setStep(2)}>
              Continue to Volume
            </button>
          </div>
        );
      case 2:
        return (
          <div className="wholesale-step-card">
            <h3>Step 2: Bulk Requirements & Estimated Pricing</h3>
            <p>Use the calculator to get an instant wholesale estimate for your order.</p>
            <div className="calculator-panel">
              <div className="calculator-item">
                <label>Desired Quantity (kg)</label>
                <input
                  type="range"
                  name="quantityKg"
                  min="50"
                  max="1000"
                  step="10"
                  value={calculator.quantityKg}
                  onChange={handleCalculatorChange}
                />
                <div className="range-value">{calculator.quantityKg} kg</div>
              </div>
              <div className="calculator-item">
                <label>Preferred Case Size</label>
                <select name="caseSize" value={calculator.caseSize} onChange={handleCalculatorChange}>
                  <option value={5}>5 kg case</option>
                  <option value={10}>10 kg case</option>
                  <option value={20}>20 kg case</option>
                  <option value={50}>50 kg case</option>
                </select>
              </div>
              <div className="calculator-summary">
                <span>Estimated rate</span>
                <strong>{estimated.priceLabel}</strong>
                <span>Total estimate</span>
                <strong>₹{estimated.total.toLocaleString("en-IN")}</strong>
              </div>
            </div>

            <div className="form-grid">
              <label>
                Monthly Volume Target
                <input
                  name="monthlyVolume"
                  value={formData.monthlyVolume}
                  onChange={handleFormChange}
                  placeholder="e.g. 500 kg"
                />
              </label>
              <label>
                Product Focus
                <input
                  name="productFocus"
                  value={formData.productFocus}
                  onChange={handleFormChange}
                  placeholder="Incense sticks, dhoop, sambrani"
                />
              </label>
              <label className="full-width">
                Additional Notes
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  placeholder="Share anything important for your quote"
                />
              </label>
            </div>
            {submitError && (
              <p className="wholesale-form-error" role="alert">
                {submitError}
              </p>
            )}
            <div className="step-actions">
              <button
                className="btn-professional-secondary"
                onClick={() => setStep(1)}
                disabled={submitting}
              >
                Back
              </button>
              <button
                className="btn-professional-primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Request Wholesale Quote"}
              </button>
            </div>
          </div>
        );
      default:
        return (
          <div className="wholesale-step-card success-card">
            <h3>Thanks — your wholesale request is in motion.</h3>
            <p>
              Our sales team will reach out within 24 hours with a custom factory-direct price proposal for your volume.
            </p>
            <div className="wholesale-summary">
              <div>
                <strong>Estimated volume:</strong> {calculator.quantityKg} kg
              </div>
              <div>
                <strong>Estimated value:</strong> ₹{estimated.total.toLocaleString("en-IN")}
              </div>
            </div>
            <button className="btn-professional-secondary" onClick={() => setStep(1)}>
              Start a new request
            </button>
          </div>
        );
    }
  };

  return (
    <div className="wholesale-page">
      <Seo {...routeMeta("/wholesale")} />

      <section className="wholesale-hero">
        <div className="hero-copy">
          <span className="eyebrow">Factory Direct Wholesale</span>
          <h1>Premium Bulk Pricing for Distributors & Retailers</h1>
          <p>
            Scale your fragrance business with NT Sticks’ high-margin wholesale program. Transparent pricing, fast minimum order fulfillment, and direct support from our Indore manufacturing team.
          </p>
          <div className="hero-cta-row">
            <div className="hero-cta-card">
              <strong>Fast MOQ</strong>
              <p>Start from 50 kg, no middleman markup.</p>
            </div>
            <div className="hero-cta-card">
              <strong>Volume Savings</strong>
              <p>Better margins as order quantities increase.</p>
            </div>
            <div className="hero-cta-card">
              <strong>Dedicated Support</strong>
              <p>Get direct access to our wholesale account team.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-tiers-section container">
        <div className="pricing-tiers-head">
          <span className="eyebrow">Published Rates</span>
          <h2>Our wholesale pricing, up front</h2>
          <p>
            No gatekeeping — these are our factory-direct rates per kilogram. Order more, pay less.
            Minimum order 50&nbsp;kg.
          </p>
        </div>
        <div className="pricing-tiers-grid">
          {baseRates.map((tier) => {
            const isActive =
              calculator.quantityKg >= tier.min && calculator.quantityKg <= tier.max;
            return (
              <div
                key={tier.min}
                className={`pricing-tier${isActive ? " is-active" : ""}`}
              >
                <span className="pricing-tier-range">
                  {tier.max === Infinity
                    ? `${tier.min}+ kg`
                    : `${tier.min === 0 ? 50 : tier.min}–${tier.max} kg`}
                </span>
                <strong className="pricing-tier-rate">₹{tier.unitPrice}</strong>
                <span className="pricing-tier-unit">per kg</span>
                {isActive && <span className="pricing-tier-flag">Your rate</span>}
              </div>
            );
          })}
        </div>
        <p className="pricing-tiers-note">
          Rates are indicative for standard packaging. Custom blends, private labelling and export
          orders are quoted separately — ask us below.
        </p>
      </section>

      <section className="wholesale-content container">
        <div className="stepper-panel">
          <div className="stepper-header">
            <span className="section-badge">Wholesale Portal</span>
            <h2>Complete the form below for a tailored bulk quote.</h2>
          </div>
          {renderStepContent()}
        </div>

        <aside className="wholesale-guide-card">
          <h3>Why choose NT Sticks wholesale?</h3>
          <ul>
            <li>Direct factory pricing from Indore for consistent aroma and quality.</li>
            <li>Flexible packaging: kg cases, retail boxes, jar formats.</li>
            <li>Low smoke, charcoal-free formulas built for modern wellness brands.</li>
            <li>Fast first-order processing for repeat distribution partners.</li>
          </ul>
          <div className="wholesale-highlight">
            <strong>Need a custom pricing sheet?</strong>
            <p>We deliver tailored quotations for hotels, souvenir chains, and spiritual product distributors.</p>
          </div>
        </aside>
      </section>
    </div>
  );
}
